import { Controller } from "@hotwired/stimulus"

// Media library modal. Opened from the image inspector and from background
// image controls; resolves to an asset id, never a URL.
export default class extends Controller {
  static targets = ["dialog", "grid", "usage", "error", "input"]

  connect() {
    this.root = this.element.closest("[data-controller~='website-builder']")
    this.root.addEventListener("builder:pick-asset", (event) => this.open(event.detail))
  }

  get builder() { return this.root.builder }

  async open(detail) {
    this.resolve = detail.onPick
    this.dialogTarget.hidden = false
    this.errorTarget.hidden = true
    await this.load()
  }

  close() {
    this.dialogTarget.hidden = true
    this.resolve = null
  }

  async load() {
    const response = await fetch(this.builder.urls.assets, { headers: { Accept: "application/json" } })
    if (!response.ok) return

    const payload = await response.json()
    this.renderGrid(payload.assets)
    this.renderUsage(payload.usage)
  }

  renderUsage(usage) {
    const mb = (bytes) => Math.round((bytes / 1048576) * 10) / 10
    this.usageTarget.textContent =
      `${usage.count}/${usage.limit} · ${mb(usage.used)}/${mb(usage.total)} MB`
  }

  renderGrid(assets) {
    if (!assets.length) {
      this.gridTarget.innerHTML =
        `<p class="col-span-3 py-8 text-center text-xs text-slate-500">${this.builder.schema.labels.ui.empty || ""}</p>`
      return
    }

    this.gridTarget.replaceChildren(...assets.map((asset) => {
      const cell = document.createElement("div")
      cell.className = "group relative overflow-hidden rounded-md border border-slate-200 dark:border-slate-700"

      const pick = document.createElement("button")
      pick.type = "button"
      pick.className = "block w-full"
      pick.addEventListener("click", () => this.pick(asset))

      const img = document.createElement("img")
      img.src = asset.thumb || asset.url
      img.alt = asset.alt || ""
      img.loading = "lazy"
      img.className = "h-20 w-full object-cover"
      pick.append(img)

      const remove = document.createElement("button")
      remove.type = "button"
      remove.textContent = "×"
      remove.className =
        "absolute right-1 top-1 hidden h-5 w-5 rounded bg-white/90 text-xs text-red-600 group-hover:block"
      remove.addEventListener("click", () => this.destroy(asset))

      cell.append(pick, remove)
      return cell
    }))
  }

  pick(asset) {
    this.resolve?.(asset)
    this.close()
  }

  async destroy(asset) {
    await fetch(`${this.builder.urls.assets}/${asset.id}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content }
    })
    await this.load()
  }

  browse() { this.inputTarget.click() }

  async upload(event) {
    const files = event.type === "drop" ? event.dataTransfer.files : event.target.files
    if (event.type === "drop") event.preventDefault()
    if (!files?.length) return

    for (const file of files) {
      const body = new FormData()
      body.append("file", file)

      const response = await fetch(this.builder.urls.assets, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
        body
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        this.errorTarget.textContent = (payload.errors || []).join(". ")
        this.errorTarget.hidden = false
      }
    }

    this.inputTarget.value = ""
    await this.load()
  }

  allowDrop(event) { event.preventDefault() }
}
